import { namespace, Point, Matrix } from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class VentilationgrillItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.VentilationgrillItem = AbstractItem.extend({

    type: 'ventilationgrill',

    supportsFreeRotation: false,

    supportedRotationSteps: [0, 45, 90, 135, 180, 225, 270, 315],

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {Ofcs.Editor.Item.DrillItem} item Me
     */

    defaults: {
        /**
         * @property {String} Type
         */
        type: 'Ventilationgrill',

        /**
         * Current position
         * @property {Ofcs.Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the shape
         * @property {Number} attributes.width
         */
        width: 490,

        /**
         * Height of the shape
         * @property {Number} attributes.height
         */
        height: 87,

        /**
         * Rotation of the shape
         * @property {Number} attributes.rotation
         */
        rotation: 0,

        /**
         * Path to item image
         * @property {String} attributes.image
         */
        image: '/bundles/ofcseditor/img/toolCover/ventilationgrill.png',

        /**
         * Defines it item is billable
         * Will be set and checked by tabletopCollisionManager
         * @property {Boolean} attributes.billable
         */
        billable: true,
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = false;
        this.supportsPositioning = true;
        this.supportsRotation = true;
        this.supportsDepth = false;
        this.supportsCollisions = true;
        this.supportsGuides = true;
        this.supportsInsideTable = 'inside';

        // Initialize paths
        this.set('ext', this.calculateSegments());
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(this.getMarginData()));

        this.on('change', this.onItemChange, this);
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return 0;
    },

    getMarginData() {
        return {
            width: 80,
            height: 50,
        };
    },

    onItemChange() {},

    /**
     * Get path segments
     *
     * @param {Object} [margin] - Safety margin
     * @return {Array}
     */
    calculateSegments(margin) {
        const path = [];
        let width;
        let height;

        if (typeof margin === 'undefined') {
            width = this.get('width');
            height = this.get('height');
        } else {
            width = this.get('width') + (margin.width * 2);
            height = this.get('height') + (margin.height * 2);
        }

        path.push(['M', -width / 2, -height / 2]);
        path.push(['L', width / 2, -height / 2]);
        path.push(['L', width / 2, height / 2]);
        path.push(['L', -width / 2, height / 2]);
        path.push(['L', -width / 2, -height / 2]);

        return [path];
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTop(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x, center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottom(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x, center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const point = new Point(center.x - (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const point = new Point(center.x + (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x - (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x + (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x - (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = withMargin ? this.get('width') + (2 * this.getMarginData().width) : this.get('width');
        const height = withMargin ? this.get('height') + (2 * this.getMarginData().height) : this.get('height');
        const point = new Point(center.x + (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get the area of the item
     *
     * @return {number}
     */
    getArea() {
        return this.get('width') * this.get('height');
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = Ofcs.Editor.Item.AbstractItem.prototype.toJSON.apply(this, args);

        return {
            x: attributes.x,
            y: attributes.y,
            width: attributes.width,
            height: attributes.height,
            rotation: attributes.rotation,
            type: attributes.type,
            billable: attributes.billable,
        };
    },

});
